import { History } from 'lucide-react';
import Modal from './Modal';
import { fmtDate } from '../../utils/format';

/**
 * The previous value of a measurement, shown under the field. Clicking it
 * opens the full history. Renders nothing until the value has changed once,
 * so an untouched grid stays clean.
 *
 * `history` is newest-first, as the API returns it.
 */
export function ChangeLogHint({ history, onOpen, className = '', compact = false }) {
  const count = history?.length || 0;
  const last = count ? history[0] : null;
  if (compact) {
    return (
      <button
        type="button"
        onClick={onOpen}
        title={count
          ? `Was ${last.oldValue || '—'}${count > 1 ? ` · ${count} changes` : ''} — open the change log`
          : 'No earlier values recorded yet'}
        className={`shrink-0 rounded p-0.5 ${count ? 'text-brand-700 hover:bg-brand-50' : 'text-content-subtle/50'} ${className}`}
      >
        <History size={11} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      title={count ? 'View the full change log' : 'No earlier values recorded yet'}
      className={`mt-0.5 flex max-w-full items-center gap-1 text-left text-2xs hover:underline ${count ? 'text-content-muted hover:text-brand-700' : 'text-content-subtle'} ${className}`}
    >
      <History size={10} className="shrink-0" />
      {count ? (
        <span className="truncate">
          was <b className="font-semibold">{last.oldValue || '—'}</b>
          {count > 1 && ` · ${count} changes`}
        </span>
      ) : (
        <span className="truncate">No earlier values</span>
      )}
    </button>
  );
}

/** Full change history for one measurement, newest first. */
export function ChangeLogModal({ title, history = [], onClose, hint }) {
  return (
    <Modal open onClose={onClose} title={title} size="sm">
      {hint && <p className="mb-2 text-xs text-content-subtle">{hint}</p>}
      {history.length === 0 ? (
        <p className="py-6 text-center text-sm text-content-subtle">No changes recorded yet.</p>
      ) : (
        <ol className="divide-y divide-line">
          {history.map((h, i) => (
            <li key={i} className="flex items-baseline justify-between gap-3 py-2 text-sm">
              <span>
                <b className="text-content">{h.oldValue || '—'}</b>
                <span className="text-content-subtle"> → </span>
                <b className="text-content">{h.newValue || '—'}</b>
              </span>
              <span className="shrink-0 text-xs text-content-subtle">{fmtDate(h.changedAt)}</span>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}

/** measurementId -> history rows, newest first. */
export function groupHistory(rows = []) {
  const byId = new Map();
  for (const h of rows) {
    const list = byId.get(h.measurementId) || [];
    list.push(h);
    byId.set(h.measurementId, list);
  }
  return byId;
}
